/**
 * @fileoverview Mouse events: PyMOL wrapper events + DOM events -> the BMaps mouse protocol
 * (`publishMouseEvent({atom}|{shape}|{background: true}, domLikeEvent)`).
 *
 * The wrapper owns camera input (rotate / pan / zoom / Meta-drag pan / Shift+wheel slab); this
 * class never forwards anything to PyMOL. It translates:
 *
 *   wrapper `pick` (a click without a drag; arrives a frame or two after pointerup, without a
 *   DOM event) -> 'mouseup' for a left click, 'contextmenu' for a right click (or a Ctrl-click
 *   that the browser turned into a context menu). ui_utils.getMouseGesture() maps exactly
 *   those two types to the 'click' and 'context-menu' gestures; a 'click' event type would be
 *   'unspecified'. WHAT was clicked is decided by the visualizer (`targetForPick`): an atom, a
 *   clickable shape, background - or "pass through" for things 3Dmol does not click (temporary
 *   molecules, shapes without onClick), which then resolve to the atom under the pointer.
 *
 *   hover. Two sources, one hovered target:
 *     - wrapper `hover` (JS projection hit-test over our visible atoms; fires only when the
 *       atom under the pointer CHANGES, with its depth);
 *     - `hitTestExtras(x, y)` on every DOM pointermove: shapes with a description and
 *       temporary-molecule atoms (PyMOL has no hover; the visualizer tests its shadow geometry).
 *   The one nearer to the camera wins, as 3Dmol's ray picks the first intersected hoverable.
 *   -> 'mouseleave' for the old target at once, 'mouseenter' for the new one after the pointer
 *   has RESTED on it (HoverDwellMs) - 3Dmol's hoverDuration behaviour, so sweeping the pointer
 *   across a protein does not raise a tooltip (and a React update) per atom crossed; DOM
 *   pointermove while a target is hovered -> 'mousemove' (the tooltip follows the pointer).
 *   The wrapper handles the same pointermove AFTER this class (its listeners were added later),
 *   so a move re-resolves once the event has been dispatched (a zero-delay timer).
 *
 *   wrapper `zoom` (Ctrl/Cmd + wheel, which the wrapper keeps away from PyMOL) -> the
 *   synthetic background event display_mgr.onBackgroundMouse reads: type 'zoom',
 *   detail { delta, ctrlKey, shiftKey, altKey, sourceEvent }.
 *
 * Position and modifier keys come from the DOM pointer events this class records itself.
 */

import { HoverDwellMs } from './PyMOLConstants';

/** Nominal radius of an atom when its depth is compared with a shape's surface (Angstrom). */
const AtomFrontOffset = 0.5;

/** Build a DOM MouseEvent where there is a DOM, else a plain object of the same shape. */
export function makeMouseEvent(type, init) {
    if (typeof MouseEvent === 'function') {
        return new MouseEvent(type, {
            bubbles: true, cancelable: true, view: typeof window !== 'undefined' ? window : null, ...init,
        });
    }
    return {
        type, ...init, preventDefault() {}, stopPropagation() {},
    };
}

function makeZoomEvent(detail) {
    if (typeof CustomEvent === 'function') return new CustomEvent('zoom', { detail });
    return { type: 'zoom', detail };
}

const NoModifiers = {
    ctrlKey: false, shiftKey: false, altKey: false, metaKey: false,
};

/** A hover candidate: `key` is the identity (vis atom, shape handle, temp atom). */
function atomCandidate(visAtom, depth) {
    return {
        key: visAtom,
        target: { atom: visAtom },
        front: Number.isFinite(depth) ? depth - AtomFrontOffset : Infinity,
    };
}

export class PyMOLPicking {
    /**
     * @param {object} options
     * @param {(target: object, event: object) => void} options.publish
     * @param {(pick: object) => object} options.targetForPick `{atom}`, `{shape}`,
     *   `{background: true}` or `{passThrough: true}` for what a click goes through
     * @param {(id: number) => (object|null)} options.atomForId
     * @param {(x: number, y: number) => ({key, target, front}|null)} [options.hitTestExtras]
     *   hoverable shapes / temporary atoms at CSS (x, y) relative to the canvas
     * @param {number} [options.hoverDwellMs] rest time before 'mouseenter'; 0 = immediately
     */
    constructor({
        publish, targetForPick, atomForId, hitTestExtras=null, hoverDwellMs=HoverDwellMs,
    }) {
        this.publish = publish;
        this.targetForPick = targetForPick;
        this.atomForId = atomForId;
        this.hitTestExtras = hitTestExtras;
        this.hoverDwellMs = hoverDwellMs;
        this.canvas = null;
        this.unsubscribe = [];
        /** Last DOM pointer state: where the pointer is, and which keys were down. */
        this.pointer = { clientX: 0, clientY: 0, ...NoModifiers };
        /** A DOM contextmenu event arrived since the last pointerdown. */
        this.sawContextMenu = false;
        /** The wrapper's current atom under the pointer ({key, target, front}) */
        this.atomHit = null;
        /** Our own hit (shape / temporary atom) under the pointer */
        this.extraHit = null;
        /** The target whose 'mouseenter' was published and whose 'mouseleave' is still owed. */
        this.hovered = null;
        /** The target under the pointer that has not been rested on long enough yet. */
        this.pending = null;
        this.dwellTimer = null;
        this.resolveTimer = null;

        this.onPointerDown = (evt) => {
            this.sawContextMenu = false;
            this.cancelPendingHover();
            this.recordPointer(evt);
        };
        this.onPointerUp = (evt) => this.recordPointer(evt);
        this.onContextMenu = () => { this.sawContextMenu = true; };
        this.onPointerMove = (evt) => {
            this.recordPointer(evt);
            if (this.pending) this.startDwell(this.pending); // still moving: start over
            if (this.hovered && !evt.buttons) {
                this.send(this.hovered.target, this.mouseEvent('mousemove', 0));
            }
            if (this.hitTestExtras && !evt.buttons) {
                const { x, y } = this.canvasPosition(evt);
                const hit = this.hitTestExtras(x, y) || null;
                const changed = (hit && hit.key) !== (this.extraHit && this.extraHit.key);
                this.extraHit = hit;
                if (changed || hit) this.scheduleResolve();
            }
        };
        this.onPointerLeave = () => {
            if (!this.extraHit) return;
            this.extraHit = null;
            this.scheduleResolve();
        };
    }

    /** DOM listeners go on the canvas as soon as it exists, before the viewer is ready. */
    attachCanvas(canvas) {
        if (!canvas || typeof canvas.addEventListener !== 'function') return;
        this.canvas = canvas;
        // Passive observers: no preventDefault / stopPropagation, so the wrapper's own input
        // handling and the app's listeners on the wrapper element are unaffected.
        canvas.addEventListener('pointerdown', this.onPointerDown);
        canvas.addEventListener('pointerup', this.onPointerUp);
        canvas.addEventListener('pointermove', this.onPointerMove);
        canvas.addEventListener('pointerleave', this.onPointerLeave);
        canvas.addEventListener('contextmenu', this.onContextMenu);
    }

    /** Subscribe to the wrapper's events. */
    attachViewer(viewer) {
        this.unsubscribe.push(
            viewer.on('pick', (pick) => this.handlePick(pick)),
            viewer.on('hover', (hover) => this.handleHover(hover)),
            viewer.on('zoom', (zoom) => this.handleZoom(zoom)),
        );
    }

    detach() {
        for (const off of this.unsubscribe) {
            if (typeof off === 'function') off();
        }
        this.unsubscribe = [];
        if (this.canvas) {
            this.canvas.removeEventListener('pointerdown', this.onPointerDown);
            this.canvas.removeEventListener('pointerup', this.onPointerUp);
            this.canvas.removeEventListener('pointermove', this.onPointerMove);
            this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
            this.canvas.removeEventListener('contextmenu', this.onContextMenu);
        }
        this.canvas = null;
        this.cancelPendingHover();
        if (this.resolveTimer != null) clearTimeout(this.resolveTimer);
        this.resolveTimer = null;
        this.hovered = null;
        this.atomHit = null;
        this.extraHit = null;
    }

    recordPointer(evt) {
        this.pointer = {
            clientX: evt.clientX,
            clientY: evt.clientY,
            ctrlKey: !!evt.ctrlKey,
            shiftKey: !!evt.shiftKey,
            altKey: !!evt.altKey,
            metaKey: !!evt.metaKey,
        };
    }

    mouseEvent(type, button, position) {
        return makeMouseEvent(type, { ...this.pointer, ...position, button });
    }

    /** Client coordinates of a point given in CSS pixels relative to the canvas. */
    clientPosition(cssX, cssY) {
        if (!this.canvas || typeof this.canvas.getBoundingClientRect !== 'function'
            || !Number.isFinite(cssX) || !Number.isFinite(cssY)) return {};
        const rect = this.canvas.getBoundingClientRect();
        return { clientX: rect.left + cssX, clientY: rect.top + cssY };
    }

    /** CSS pixels relative to the canvas of a DOM pointer event. */
    canvasPosition(evt) {
        const rect = this.canvas && typeof this.canvas.getBoundingClientRect === 'function'
            ? this.canvas.getBoundingClientRect() : { left: 0, top: 0 };
        return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    }

    send(target, event) {
        try {
            this.publish(target, event);
        } catch (ex) {
            console.error(`Exception in mouse event handler ${event.type}`, ex);
        }
    }

    /**
     * Something went away (atom removed or hidden, shape / temporary molecule removed, scene
     * reset): no leave event, just forget it. `key` null = forget everything.
     */
    forgetTarget(key) {
        const gone = (candidate) => candidate && (!key || candidate.key === key);
        if (gone(this.hovered)) this.hovered = null;
        if (gone(this.pending)) this.cancelPendingHover();
        if (gone(this.atomHit)) this.atomHit = null;
        if (gone(this.extraHit)) this.extraHit = null;
    }

    /** Forget every hover target whose key passes `test` (a removed temporary molecule's atoms). */
    forgetTargetsWhere(test) {
        for (const candidate of [this.hovered, this.pending, this.atomHit, this.extraHit]) {
            if (candidate && test(candidate.key)) this.forgetTarget(candidate.key);
        }
    }

    /** M1 name, kept for callers that forget atoms. */
    forgetAtom(visAtom) { this.forgetTarget(visAtom); }

    cancelPendingHover() {
        if (this.dwellTimer != null) clearTimeout(this.dwellTimer);
        this.dwellTimer = null;
        this.pending = null;
    }

    startDwell(candidate) {
        this.cancelPendingHover();
        this.pending = candidate;
        if (this.hoverDwellMs > 0) {
            this.dwellTimer = setTimeout(() => this.enterPending(), this.hoverDwellMs);
        } else {
            this.enterPending();
        }
    }

    enterPending() {
        const candidate = this.pending;
        this.cancelPendingHover();
        if (!candidate) return;
        this.hovered = candidate;
        this.send(candidate.target, this.mouseEvent('mouseenter', 0));
    }

    scheduleResolve() {
        if (this.resolveTimer != null) return;
        this.resolveTimer = setTimeout(() => {
            this.resolveTimer = null;
            this.resolveHover();
        }, 0);
    }

    /** The front-most of the atom hit and our own hit is the hovered target. */
    resolveHover() {
        const a = this.atomHit;
        const e = this.extraHit;
        let current = a || e;
        if (a && e) current = e.front <= a.front ? e : a;
        const key = current ? current.key : null;
        if (this.pending && this.pending.key === key) return; // still resting on it
        if (this.hovered && this.hovered.key === key) {
            this.cancelPendingHover();
            return;
        }
        this.cancelPendingHover();
        if (this.hovered) {
            const left = this.hovered;
            this.hovered = null;
            this.send(left.target, this.mouseEvent('mouseleave', 0));
        }
        if (current) this.startDwell(current);
    }

    handlePick(pick) {
        if (!pick) return;
        const clickName = String(pick.click || 'single_left');
        if (clickName.includes('middle')) return;
        const isContextMenu = this.sawContextMenu || clickName.includes('right');
        this.sawContextMenu = false;
        const event = isContextMenu
            ? this.mouseEvent('contextmenu', 2, this.clientPosition(pick.cssX, pick.cssY))
            : this.mouseEvent('mouseup', 0, this.clientPosition(pick.cssX, pick.cssY));

        let target = this.targetForPick(pick) || { background: true };
        if (target.passThrough) {
            // 3Dmol: the click goes to the next clickable thing along the ray - approximated by
            // the atom the wrapper's hover hit-test has under the pointer
            target = this.atomHit ? { atom: this.atomHit.key } : { background: true };
        }
        this.send(target, event);
    }

    /** The wrapper reports the atom under the pointer whenever that changes (null = none). */
    handleHover(hover) {
        const visAtom = hover ? this.atomForId(hover.id) : null;
        this.atomHit = visAtom ? atomCandidate(visAtom, hover.depth) : null;
        this.resolveHover();
    }

    handleZoom(zoom) {
        if (!zoom) return;
        const source = zoom.event || {};
        this.send({ background: true }, makeZoomEvent({
            // The wheel's own deltaY, which is the convention every backend reports in
            // (VisualizerInterface.RegisterMouseHandler): negative for wheel up / pinch out.
            // display_mgr.bindingSiteStepForZoom maps that to a larger binding-site radius.
            delta: zoom.deltaY,
            // The wrapper raises `zoom` for Ctrl+wheel AND Cmd+wheel; display_mgr only looks at
            // ctrlKey, so Cmd counts as Ctrl here (on a Mac a pinch arrives as Ctrl+wheel).
            ctrlKey: !!(source.ctrlKey || source.metaKey),
            shiftKey: !!source.shiftKey,
            altKey: !!source.altKey,
            sourceEvent: source,
        }));
    }
}
